import { ApolloClient, NormalizedCacheObject } from '@apollo/client'

import {
  GetListingReviewsDocument,
  GetListingAdventuresByProximityDocument,
  GetListingPhotosDocument,
  SuggestionsDocument,
} from 'hooks/useQueries'

type ListingSsr = {
  reviews?: unknown
  adventures?: unknown
  photos?: unknown
  suggestions?: unknown
}

type SeedListing = {
  id?: string | number
}

// Variables must match the consumers exactly so the seed shares their Apollo
// cache key. Reviews (Sections/ListingReviews) and the mobile gallery
// (Header/ListingMobileGallery) pass `listing.id` raw; adventures
// (Adventures/AdventureCardList) passes `listing.id.toString()`. The id comes
// from the same prehydrated listing prop the page reads. writeQuery is
// synchronous — safe in mini_racer SSR.
const seedListing = (
  client: ApolloClient<NormalizedCacheObject>,
  ssr: ListingSsr | undefined,
  listing: SeedListing | undefined,
): void => {
  if (!ssr || listing?.id == null) return

  const { id } = listing

  if (ssr.reviews) {
    try {
      client.writeQuery({
        query: GetListingReviewsDocument,
        variables: { listingId: id },
        data: ssr.reviews,
      })
    } catch {
      // Best-effort: a malformed payload must not break the render.
    }
  }

  if (ssr.adventures) {
    try {
      client.writeQuery({
        query: GetListingAdventuresByProximityDocument,
        variables: { listingId: id.toString() },
        data: ssr.adventures,
      })
    } catch {
      // Best-effort: a malformed payload must not break the render.
    }
  }

  if (ssr.photos) {
    try {
      client.writeQuery({
        query: GetListingPhotosDocument,
        variables: { listingId: id },
        data: ssr.photos,
      })
    } catch {
      // Best-effort: a malformed payload must not break the render.
    }
  }

  // Suggestions ("More <region> Escapes") reads { listingId, scope: 'region' };
  // cursor is omitted and first: 3 is baked into the query, so neither is a key.
  if (ssr.suggestions) {
    try {
      client.writeQuery({
        query: SuggestionsDocument,
        variables: { listingId: id, scope: 'region' },
        data: ssr.suggestions,
      })
    } catch {
      // Best-effort: a malformed payload must not break the render.
    }
  }
}

export default seedListing
